import React, { useId } from 'react';
import './SolarInverterGridFlow.css';

/**
 * SolarInverterGridFlow — rrjedhje Solar → Inverter → GRID (i njëjti si unisolar_ballina).
 */
export const SOLAR_INVERTER_GRID_SPECS = {
  web: {
    containerMaxWidth: 760,
    containerPaddingX: 32,
    containerPaddingY: 32,
    cardSize: 112,
    cardBorderRadius: 16,
    flexGap: 8,
    connectorWidth: 128,
    connectorHeight: 56,
    footerFontSize: 14,
    labelFontSize: 14,
    mwBadgeFontSize: 13,
    mwBadgePaddingX: 10,
    mwBadgePaddingY: 5,
    summaryMarginTop: 24,
  },
  mobile: {
    containerPaddingX: 16,
    containerPaddingY: 20,
    cardSize: 80,
    cardBorderRadius: 12,
    gapVerticalBetweenCards: 48,
    connectorVerticalHeight: 40,
    footerFontSize: 12,
    labelFontSize: 12,
    mwBadgeFontSize: 11,
    mwBadgePaddingX: 8,
    mwBadgePaddingY: 4,
    summaryMarginTop: 16,
  },
  colors: {
    solar: '#FF8C00',
    inverter: '#00CED1',
    grid: '#FFFFFF',
    overlay: 'rgba(0,0,0,0.6)',
    badgeText: '#4CAF50',
    badgeBorder: '#4CAF50',
  },
};

const SolarIcon = ({ idPrefix }) => (
  <svg viewBox="0 0 64 64" className="sigf-icon" aria-hidden="true">
    <defs>
      <linearGradient id={`${idPrefix}-sigf-solar-grad`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#FFD27A" />
        <stop offset="100%" stopColor="#FF8C00" />
      </linearGradient>
    </defs>
    <circle cx="32" cy="20" r="6" fill={`url(#${idPrefix}-sigf-solar-grad)`} />
    <g stroke={`url(#${idPrefix}-sigf-solar-grad)`} strokeWidth="2" strokeLinecap="round">
      <line x1="32" y1="6" x2="32" y2="10" />
      <line x1="18" y1="20" x2="22" y2="20" />
      <line x1="46" y1="20" x2="42" y2="20" />
      <line x1="22" y1="10" x2="25" y2="13" />
      <line x1="42" y1="10" x2="39" y2="13" />
    </g>
    <g
      stroke={`url(#${idPrefix}-sigf-solar-grad)`}
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 52 L20 32 L44 32 L50 52 Z" />
      <line x1="22" y1="38" x2="42" y2="38" />
      <line x1="20" y1="44" x2="44" y2="44" />
      <line x1="32" y1="32" x2="32" y2="52" />
    </g>
  </svg>
);

const InverterIcon = ({ idPrefix }) => (
  <svg viewBox="0 0 64 64" className="sigf-icon" aria-hidden="true">
    <defs>
      <linearGradient id={`${idPrefix}-sigf-inv-grad`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#7CFFFF" />
        <stop offset="100%" stopColor="#00CED1" />
      </linearGradient>
      <filter id={`${idPrefix}-sigf-inv-glow`} x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="1.5" result="b" />
        <feMerge>
          <feMergeNode in="b" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
    <rect
      x="14"
      y="16"
      width="36"
      height="32"
      rx="6"
      fill="none"
      stroke={`url(#${idPrefix}-sigf-inv-grad)`}
      strokeWidth="2"
    />
    <path
      d="M20 32 Q24 24, 28 32 T36 32 T44 32"
      fill="none"
      stroke={`url(#${idPrefix}-sigf-inv-grad)`}
      strokeWidth="2.2"
      strokeLinecap="round"
    />
    <circle cx="44" cy="24" r="3" fill="#38bdf8" filter={`url(#${idPrefix}-sigf-inv-glow)`} />
  </svg>
);

const GridIcon = () => (
  <svg viewBox="0 0 64 64" className="sigf-icon" aria-hidden="true">
    <g stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M32 8 L20 56 L44 56 Z" />
      <line x1="26" y1="32" x2="38" y2="32" />
      <line x1="23" y1="44" x2="41" y2="44" />
      <line x1="28" y1="20" x2="36" y2="20" />
      <line x1="14" y1="22" x2="50" y2="22" />
    </g>
  </svg>
);

const EnergyConduit = ({ flowActive }) => (
  <div className={`sigf-conduit ${flowActive ? 'is-flowing' : ''}`} aria-hidden="true">
    <div className="sigf-conduit-line" />
    <span className="sigf-particle sigf-particle--1" />
    <span className="sigf-particle sigf-particle--2" />
    <span className="sigf-particle sigf-particle--3" />
    <span className="sigf-particle sigf-particle--4" />
    <span className="sigf-particle sigf-particle--5" />
  </div>
);

const Pod = ({ tone, label, children, testId, isCentral }) => (
  <div
    className={`sigf-pod sigf-pod--${tone} ${isCentral ? 'sigf-pod--central' : ''}`}
    data-testid={testId}
  >
    <div className="sigf-pod-stage">
      <div className="sigf-pod-halo" />
      <div className="sigf-pod-ring" />
      <div className="sigf-pod-glass" />
      <div className="sigf-pod-shine" />
      <div className="sigf-pod-icon">{children}</div>
      <span className="sigf-pod-dot" />
    </div>
    <div className="sigf-pod-label">{label}</div>
  </div>
);

const SolarInverterGridFlow = ({
  totalMw = '',
  inverterCount = 0,
  gridMw = '0.00',
  gridUnit = 'MW',
  flowActive = true,
  backgroundImageUrl,
  exportTagLabel = 'EXPORT',
  /** Kur është false (p.sh. matës energjie jo-HIB): vetëm vlera MW/kW, pa shigjetë dhe pa pill EXPORT */
  showExportTag = true,
  inverterLabel = 'Inverter',
  showSummaryFooter = false,
  brandLabel = '',
  className = '',
}) => {
  const uid = useId().replace(/:/g, '');
  const idPrefix = `sigf-${uid}`;

  const summaryLine =
    showSummaryFooter && (String(totalMw || '').trim() !== '' || Number(inverterCount) > 0);

  return (
    <div className={['sigf-root', className].filter(Boolean).join(' ')} data-testid="solar-inverter-grid-flow">
      <div
        className="sigf-scene"
        data-has-bg={!!backgroundImageUrl}
        style={backgroundImageUrl ? { '--sigf-bg-image': `url(${backgroundImageUrl})` } : undefined}
      >
        {backgroundImageUrl && <div className="sigf-bg-photo" aria-hidden="true" />}
        <div className="sigf-bg-glow" aria-hidden="true" />

        <div className="sigf-stage">
          <Pod tone="solar" label="Solar" testId="card-solar">
            <SolarIcon idPrefix={idPrefix} />
          </Pod>

          <EnergyConduit flowActive={flowActive} />

          <Pod tone="inverter" label={inverterLabel} testId="card-inverter" isCentral>
            <InverterIcon idPrefix={idPrefix} />
          </Pod>

          <EnergyConduit flowActive={flowActive} />

          <Pod tone="grid" label="GRID" testId="card-grid">
            <GridIcon />
          </Pod>
        </div>

        <div className="sigf-footer-stack">
          <div className="sigf-footer-bar">
            {brandLabel ? (
              <p className="sigf-brand" data-testid="sigf-brand">
                {brandLabel}
              </p>
            ) : (
              <span className="sigf-brand-spacer" aria-hidden="true" />
            )}
            <div
              className={['sigf-mw-badge', !showExportTag ? 'sigf-mw-badge--value-only' : ''].filter(Boolean).join(' ')}
              data-testid="grid-mw-badge"
            >
              {showExportTag ? <span className="sigf-mw-badge-glow" aria-hidden="true" /> : null}
              {showExportTag ? (
                <svg className="sigf-mw-badge-arrow" viewBox="0 0 14 14" aria-hidden="true">
                  <path
                    d="M7 1.5 L11.5 6 L8.6 6 L8.6 12.5 L5.4 12.5 L5.4 6 L2.5 6 Z"
                    fill="currentColor"
                  />
                </svg>
              ) : null}
              <span className="sigf-mw-badge-value">
                {gridMw} {gridUnit}
              </span>
              {showExportTag ? <span className="sigf-mw-badge-tag">{exportTagLabel}</span> : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SolarInverterGridFlow;
